import { AuthProvider, UserRole, User, UserWithProfiles, UserProfile, DigiAuthUser } from "../types/user"
import { InvestmentStrategyVariant, InvestmentStrategy } from "../types/investment"
import { Loan } from "../types/loan";

type UserDataType = {
  userId: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  role: UserRole | "";
}

type EventIdentifyUserType = UserDataType & {
  event: "identify_user";
  company: string;
  entityType: EntityType;
}

type EntityType = "legal" | "private" | ""

type EventRegisterType = UserDataType & {
  event: "register";
  logInMethod: AuthProvider | "";
}

type EventLoginType = UserDataType & {
  event: "log_in";
  logInMethod: AuthProvider | "";
}

type EventRegisterCompanyType = {
  event: "register_company";
  userId: string;
}

type EventOnboardingCompletedType = {
  event: "onboarding_completed";
  userId: string
}

type Onboardingstep =
  | "personal_details"
  | "identity_verification"
  | "address"
  | "cashflow"
  | "additional_data"
  | "bank_documents"

type EventOnboardingMilestoneType = {
  event: "onboarding_milestone";
  userId: string;
  milestone: Onboardingstep | ""
}

type InvestmentStrategyEventNameType = "investment_strategy_created" | "investment_strategy_signed"

type EventInvestmentStrategyType = {
  event: InvestmentStrategyEventNameType | "";
  userId: string;
  monthlyInvestmentLimit: number | null;
  maximumInvestmentPerLoan: number;
  investmentPeriod: number;
  investmentRisk: InvestmentStrategyVariant | "";
}

type EventInvestmentDepositInitiatedType = {
  event: "investment_deposit_initiated";
  userId: string;
  amount: number;
}

type EventLoanApplicationStartedType = {
  event: "loan_application_started";
}

type EventLoanApplicationFilledType = {
  event: "loan_application_filled";
  amount: number;
  period: number;
  loanType: LoanPurpose;
}

type EventLoanApplicationSubmittedType = {
  event: "loan_application_submitted";
  userId: string;
  amount: number;
  period: number;
  loanType: LoanPurpose;
}

type EventLoanApplicationSignedType = {
  event: "loan_application_signed";
  userId: string;
  amount: number;
  period: number;
  loanType: LoanPurpose;
}

type EventContentType =
  | EventIdentifyUserType
  | EventRegisterType
  | EventLoginType
  | EventRegisterCompanyType
  | EventOnboardingCompletedType
  | EventOnboardingMilestoneType
  | EventInvestmentStrategyType
  | EventInvestmentDepositInitiatedType
  | EventLoanApplicationStartedType
  | EventLoanApplicationFilledType
  | EventLoanApplicationSubmittedType
  | EventLoanApplicationSignedType

export const trackGAEventforPHD = (eventData: EventContentType): void => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(eventData);
};

export const generateIdentifyUserEventObject = (user: UserWithProfiles | UserProfile, role: UserRole, company: string, entityType: EntityType ): EventIdentifyUserType => {
  return {
    event: "identify_user",
    userId: user.mzid || "",
    firstName: user.firstName || "",
    lastName: user.lastName || "",
    company: company,
    email: user.email || "",
    phone: (('phoneCode' in user) ? user.phoneCode + user.phoneNumber : user.fullPhoneNumber) || "",
    role: role,
    entityType: entityType
  }
}

export const generateRegisterByIdEventObject = (user: DigiAuthUser): EventRegisterType => {
  return {
    event: "register",
    userId: "",
    firstName: "",
    lastName: "",
    email: "",
    phone: user.phoneNumber ? "+372" + user.phoneNumber : "",
    logInMethod: user.provider,
    role: user.role
  }
}

export const generateRegisterByEmailEventObject = (user: User): EventRegisterType => {
  return {
    event: "register",
    userId: user.mzid || "",
    firstName: user.firstName || "",
    lastName: user.lastName || "",
    email: user.email || "",
    phone: user.fullPhoneNumber || "",
    logInMethod: user.authProvider || "",
    role: user.role || ""
  }
}

export const generateLogInEventObject = (user: User): EventLoginType => {
  return {
    event: "log_in",
    userId: user.mzid || "",
    firstName: user.firstName || "",
    lastName: user.lastName || "",
    email: user.email || "",
    phone: user.fullPhoneNumber || "",
    logInMethod: user.authProvider,
    role: user.role
  }
}

export const generateRegisterCompanyEventObject = (userId: string): EventRegisterCompanyType => {
  return {
    event: "register_company",
    userId: userId
  }
}

export const generateOnboardingCompletedEventObject = (userId: string): EventOnboardingCompletedType => {
  return {
    event: "onboarding_completed",
    userId: userId
  }
}

export const generateOnboardingMilestoneEventObject = (userId: string, step: string): EventOnboardingMilestoneType => {
  return {
    event: "onboarding_milestone",
    userId: userId,
    milestone: convertMilestoneName(step)
  }
}

const convertMilestoneName = (stepName: string): Onboardingstep => {
  const conversions: { [key: string]: Onboardingstep } = {
    "personal": "personal_details",
    "verification": "identity_verification",
    "address": "address",
    "cashflow" : "cashflow",
    "additional_data" : "additional_data",
    "bank_documents" : "bank_documents"
  };

  const result = conversions[stepName];

  if (!result) {
    throw new Error(`Unrecognized registration step name: ${stepName}`);
  }

  return result || "";
};

export const generateInvestmentStrategyEventObject = (eventName: InvestmentStrategyEventNameType, userId: string, strategy: InvestmentStrategy, monthlyUnlimited?: boolean): EventInvestmentStrategyType => {
  return {
    event: eventName,
    userId: userId,
    monthlyInvestmentLimit: monthlyUnlimited ? null : strategy.monthlyAmount,
    maximumInvestmentPerLoan:  strategy.amountPerLoan,
    investmentPeriod: strategy.maxPeriod / 12,
    investmentRisk: strategy.variant
  }
}

export const generateInvestmentDepositInitiatedEventObject = (userId: string, amount: number): EventInvestmentDepositInitiatedType => {
  return {
    event: "investment_deposit_initiated",
    userId: userId,
    amount: amount
  }
}

export const generateLoanApplicationStartedEventObject = (): EventLoanApplicationStartedType => {
  return {
    event: "loan_application_started"
  }
}

export const generateLoanApplicationFilledEventObject = (loan: Loan): EventLoanApplicationFilledType => {
  return {
    event: "loan_application_filled",
    amount: loan.amount,
    period: loan.period,
    loanType: loan.purpose,
  }
}

export const generateLoanApplicationSubmittedEventObject = (userId: string, loan: Loan): EventLoanApplicationSubmittedType => {
  return {
    event: "loan_application_submitted",
    userId: userId,
    amount: loan.amount,
    period: loan.period,
    loanType: loan.purpose,
  }
}

export const generateLoanApplicationSignedEventObject = (userId: string, loan: Loan): EventLoanApplicationSignedType => {
  return {
    event: "loan_application_signed",
    userId: userId,
    amount: loan.amount,
    period: loan.period,
    loanType: loan.purpose,
  }
}
